import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useDispatch } from '../shell/dispatchContext';
import { ordersApi, type QueuedOrder } from './api';

/**
 * The live queue, and the order open beside it.
 *
 * Updates over SignalR rather than polling. A dispatcher watches this all shift, and a
 * five-second poll is both slower to react and heavier on the server than one open
 * connection that says what changed.
 *
 * A hook rather than a component because the map view needs the open order without the
 * queue around it — the detail floating over the tiles, and the automation flag read from
 * the queue — and a second copy of this logic would be the one that forgot to refetch the
 * open order.
 */
export function useQueue() {
  const { selectedOrder, setSelectedOrder } = useDispatch();

  const [orders, setOrders] = useState<QueuedOrder[]>([]);
  const [loading, setLoading] = useState(true);

  // The open order's id, for the refetch below. A ref rather than a dependency, so that
  // `reload` keeps its identity and the hub subscription is not torn down and rebuilt every
  // time somebody selects a row.
  const open = useRef<string | null>(null);

  useEffect(() => {
    open.current = selectedOrder?.id ?? null;
  });

  const refresh = useCallback(async () => {
    try {
      setOrders(await ordersApi.queue());
    } finally {
      setLoading(false);
    }
  }, []);

  const select = useCallback(
    async (orderId: string) => {
      setSelectedOrder(await ordersApi.get(orderId));
    },
    [setSelectedOrder],
  );

  /**
   * The open order is refetched alongside the queue, so a driver accepting or declining
   * appears in the detail without the dispatcher touching anything. Updating the queue
   * around a stale detail would be worse than not updating at all — the row would say
   * assigned while the panel still offered to assign it.
   */
  const reload = useCallback(async () => {
    await refresh();

    const id = open.current;

    if (id !== null) await select(id);
  }, [refresh, select]);

  // Refetching on reconnect as well as on an event: while the socket was down the queue
  // may have changed in ways no delta will now arrive to describe.
  useDispatcherHub({ onOrderChanged: reload, onReconnected: reload });

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const close = useCallback(() => setSelectedOrder(null), [setSelectedOrder]);

  const cancelled = useCallback(() => {
    setSelectedOrder(null);
    void refresh();
  }, [setSelectedOrder, refresh]);

  return { orders, loading, selected: selectedOrder, select, close, cancelled, refresh, reload };
}
